import React from 'react';
import styles from './ParametersSummary.module.scss';
import { CALCULATION_ZONE_CONFIG } from '../../../utils/constants';
import { getZoneValue } from '../../../utils';

// Placeholder constants for missing values
const FORM_INPUT_TEXTS = {
  labels: {
    description: 'Description',
    divisorDetermination: 'Divisor Determination',
  },
  placeholders: {
    descriptions: 'Enter description',
    divisorDetermination: 'Select divisor determination',
  },
  required: {
    divisorDeterminationPoint: 'Divisor determination point is required',
  },
};

const EOD = 'EOD';
const EOD_INTRADAY = 'EOD_INTRADAY';

export const INCEPTION_RETURN_TYPE_MAPPING = {
  PRICE_RETURN: 'Price Return',
  TOTAL_RETURN: 'Total Return',
  NET_RETURN: 'Net Return',
};

const CALCULATION_FREQUENCY_MAPPING = {
  [EOD]: 'EOD only',
  [EOD_INTRADAY]: 'EOD and Intraday',
};

const ParametersSummary = ({
  indexMetadata,
  startingValueToggle,
  divisorToggle,
  divisorDeterminationPoint,
  calculationZoneMap,
}) => {
  return (
    <div className={styles.rightContainer}>
      <div className={styles.rightContainerTitle}>Parameters Summary</div>
      <div className={styles.rightContainerContent}>
        <div className={styles.rightContainerContentItem}>
          <div>Name:</div>
          <div>{indexMetadata?.name || '-'}</div>
        </div>
        <div className={styles.rightContainerContentItem}>
          <div>Description:</div>
          <div>
            {indexMetadata?.description ? (
              <p className={styles.paragraph}>{indexMetadata.description}</p>
            ) : (
              '-'
            )}
          </div>
        </div>
        <div className={styles.rightContainerContentItem}>
          <div>Prefix:</div>
          <div>{indexMetadata?.org_prefix || '-'}</div>
        </div>
        <div className={styles.rightContainerContentItem}>
          <div>Ticker:</div>
          <div>{indexMetadata?.symbol || '-'}</div>
        </div>

        {startingValueToggle && !divisorToggle && (
          <>
            <div className={styles.rightContainerContentItem}>
              <div>Starting Value:</div>
              <div>{indexMetadata?.startingValue || '-'}</div>
            </div>
            <div className={styles.rightContainerContentItem}>
              <div>{FORM_INPUT_TEXTS.labels.divisorDetermination}:</div>
              <div>{divisorDeterminationPoint ?? '-'}</div>
            </div>
          </>
        )}

        {divisorToggle && !startingValueToggle && (
          <div className={styles.rightContainerContentItem}>
            <div>Divisor:</div>
            <div>{indexMetadata?.divisor || '-'}</div>
          </div>
        )}

        <div className={styles.rightContainerContentItem}>
          <div>Currency:</div>
          <div>{indexMetadata?.currency || '-'}</div>
        </div>
        <div className={styles.rightContainerContentItem}>
          <div>Calculation Frequency:</div>
          <div>
            {indexMetadata?.calculation_setup
              ? CALCULATION_FREQUENCY_MAPPING[indexMetadata?.calculation_setup]
              : '-'}
          </div>
        </div>
        <div className={styles.rightContainerContentItem}>
          <div>Timezone:</div>
          <div>
            {getZoneValue(calculationZoneMap, indexMetadata, CALCULATION_ZONE_CONFIG.CALC_TIMEZONE)}
          </div>
        </div>
        <div className={styles.rightContainerContentItem}>
          <div>Calculation Start:</div>
          <div>
            {getZoneValue(
              calculationZoneMap,
              indexMetadata,
              CALCULATION_ZONE_CONFIG.CALC_OPEN_TIME,
            )}
          </div>
        </div>
        <div className={styles.rightContainerContentItem}>
          <div>Calculation End:</div>
          <div>
            {getZoneValue(
              calculationZoneMap,
              indexMetadata,
              CALCULATION_ZONE_CONFIG.CALC_CLOSE_TIME,
            )}
          </div>
        </div>
        <div className={styles.rightContainerContentItem}>
          <div>Start Week Day:</div>
          <div>
            {getZoneValue(
              calculationZoneMap,
              indexMetadata,
              CALCULATION_ZONE_CONFIG.CALC_START_WEEK_DAY,
              true,
            )}
          </div>
        </div>
        <div className={styles.rightContainerContentItem}>
          <div>End Week Day:</div>
          <div>
            {getZoneValue(
              calculationZoneMap,
              indexMetadata,
              CALCULATION_ZONE_CONFIG.CALC_END_WEEK_DAY,
              true,
            )}
          </div>
        </div>
        <div className={styles.rightContainerContentItem}>
          <div>Calculation EOD Time: </div>
          <div>
            {getZoneValue(calculationZoneMap, indexMetadata, CALCULATION_ZONE_CONFIG.CALC_EOD_TIME)}
          </div>
        </div>

        <div className={styles.rightContainerContentItem}>
          <div>Return Type:</div>
          <div>{INCEPTION_RETURN_TYPE_MAPPING[indexMetadata?.calculation_type || ''] || '-'}</div>
        </div>

        {/* Interest Rate on Cash - only show if cash constituent exists */}
        {indexMetadata?.additional_settings?.cash_constituent && (
          <div className={styles.rightContainerContentItem}>
            <div>Interest Rate on Cash:</div>
            <div>
              {indexMetadata?.additional_settings?.cash_constituent?.interest_rate_on_cash || 0}
            </div>
          </div>
        )}

        {/* Annualized Daily Decrement Percentage - only show if toggle is on */}
        {indexMetadata?.additional_settings?.annualized_daily_decrement && (
          <div className={styles.rightContainerContentItem}>
            <div>Annualized Daily Decrement Percentage:</div>
            <div>
              {indexMetadata?.additional_settings?.decrement_value_percentage
                ? (indexMetadata?.additional_settings?.decrement_value_percentage * 100).toFixed(2)
                : 0}
              %
            </div>
          </div>
        )}

        <div className={styles.rightContainerContentItem}>
          <div>Launch Date:</div>
          <div>
            {indexMetadata?.launch_date
              ? typeof indexMetadata.launch_date === 'string'
                ? indexMetadata.launch_date
                : indexMetadata.launch_date.toISOString().split('T')[0]
              : '-'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParametersSummary;
